type ParsedSiteUrl = {
  domain: string;
  port?: string;
};

function parseSiteUrl(rawUrl: string): ParsedSiteUrl | null {
  try {
    const normalizedUrl = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
    const parsed = new URL(normalizedUrl);
    let domain = parsed.hostname.toLowerCase();
    if (domain.startsWith('www.')) {
      domain = domain.slice(4);
    }
    return {
      domain,
      port: parsed.port || undefined,
    };
  } catch {
    return null;
  }
}

function getRuntimeHostname(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.hostname.toLowerCase();
}

function isStagingHost(hostname?: string): boolean {
  if (!hostname) return false;
  return (
    hostname === 'staging.documitra.com' ||
    hostname.endsWith('.staging.documitra.com') ||
    hostname.includes('staging')
  );
}

function isLocalHost(hostname?: string): boolean {
  if (!hostname) return false;
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0'
  );
}

export function getPartnerRootDomain(): string {
  const explicit = process.env.REACT_APP_PARTNER_ROOT_DOMAIN?.trim().toLowerCase();
  const runtimeHostname = getRuntimeHostname();
  const apiUrl = process.env.REACT_APP_API_URL?.trim().toLowerCase() || '';
  const appEnv = process.env.REACT_APP_ENV?.trim().toLowerCase();
  const stagingDetected =
    appEnv === 'staging' ||
    apiUrl.includes('staging.api.documitra.com') ||
    isStagingHost(runtimeHostname);

  if (explicit && !(stagingDetected && explicit === 'localhost')) {
    return explicit;
  }

  if (stagingDetected) {
    return 'staging.documitra.com';
  }

  const frontendUrl = process.env.REACT_APP_FRONTEND_URL?.trim();
  if (frontendUrl) {
    const parsed = parseSiteUrl(frontendUrl);
    if (parsed?.domain) return parsed.domain;
  }

  if (
    appEnv === 'development' ||
    process.env.NODE_ENV === 'development' ||
    isLocalHost(runtimeHostname)
  ) {
    return 'localhost';
  }

  return 'documitra.com';
}

export function getPartnerRootPort(): string | undefined {
  const rootDomain = getPartnerRootDomain();
  if (rootDomain !== 'localhost') return undefined;

  const explicit = process.env.REACT_APP_PARTNER_ROOT_PORT?.trim();
  if (explicit) return explicit;

  const frontendUrl = process.env.REACT_APP_FRONTEND_URL?.trim();
  if (frontendUrl) {
    const parsed = parseSiteUrl(frontendUrl);
    if (parsed?.domain === 'localhost' && parsed.port) {
      return parsed.port;
    }
  }

  if (process.env.REACT_APP_ENV === 'development' || process.env.NODE_ENV === 'development') {
    return '3000';
  }

  return undefined;
}

export function getPartnerRootDomainSuffix(): string {
  const rootDomain = getPartnerRootDomain();
  const port = getPartnerRootPort();
  const portSuffix = rootDomain === 'localhost' && port ? `:${port}` : '';
  return `.${rootDomain}${portSuffix}`;
}

export function buildPartnerSubdomainUrl(subdomain: string): string {
  const normalized = subdomain.trim().toLowerCase();
  const rootDomain = getPartnerRootDomain();
  const port = getPartnerRootPort();
  const protocol = rootDomain === 'localhost' ? 'http' : 'https';
  const portSuffix = rootDomain === 'localhost' && port ? `:${port}` : '';

  return `${protocol}://${normalized}.${rootDomain}${portSuffix}`;
}

export function formatPartnerReferralDomain(subdomain: string): string {
  return `${subdomain.trim().toLowerCase()}${getPartnerRootDomainSuffix()}`;
}
